<script setup>
// TapDatePicker — THE date field (mockup .tdp kit, admin-club-meet-form-when-
// oneoff.html): a typed, masked half plus a compact calendar popover with
// quick chips, month and year selects, ‹ › month nav and a hand-rolled month
// grid — 36px targets, today ringed in brand, selected brand-filled,
// out-of-range days disabled, adjacent-month days muted but tappable.
//
// v-model is an ISO string, NOT the text in the box. The mask, placeholder and
// field order all come from the reader's locale via useDates, and the LABEL
// always states the order it wants ("Start date (dd/mm/yyyy)" · "(mm/dd/yyyy)")
// — a box holding "08/12/2026" with nothing saying the order is exactly how
// the wrong date gets read. So every use passes a `label`.
//
// THE DEFAULTS ARE SCHEDULING DEFAULTS: `min` is today and the chips are
// Today / Tomorrow / In a week, because this started life as the meet-date
// field. Any question about a date in the PAST must say so — `min` and `max`
// bound the window and `quick` replaces or empties the chip row. A birthday
// asked with the defaults has every day disabled and cannot be answered at
// all, which is exactly what shipped before this was fixed.
//
// The month and year SELECTS exist because ‹ › alone is a month per click:
// reaching a 1988 birthday took about 450 of them. They are offered only
// within the window, so the grid can never be scrolled somewhere nothing is
// selectable.
import {computed, ref, watch} from 'vue';
import {vMaska} from 'maska/vue';
import {useDates} from '~/composables/useDates';
import {useDeviceLocale} from '~/composables/useDeviceLocale';
import {dateOnlyForPicker, toDateOnly} from '~/utils/dateOnly';
import TapDatePickerCalendar from '~/components/fields/TapDatePickerCalendar.vue';

const props = defineProps({
  label: {type: String, default: null},
  error: {type: Boolean, default: false},
  errorMessages: {type: [Array, String], default: () => []},
  hint: {type: String, default: null},
  /** BCP-47 tag. Defaults to the device locale; pass one to override. */
  locale: {type: String, default: null},
  // An ISO date — days before it are disabled. Defaults to today; pass an
  // empty string to allow any date.
  min: {type: String, default: undefined},
  // An ISO date — days AFTER it are disabled. No maximum by default; pass
  // today's date for a question that cannot be answered with the future.
  max: {type: String, default: undefined},
  // The chip row. Days are counted from today, so these only make sense for a
  // date near today; pass [] to drop the row entirely.
  // A date of birth. With no answer yet the popover opens on a list of years,
  // about 25 years back, and today is not ringed: opening on today made today
  // the easiest pick. Every other use keeps the month grid.
  birthDate: {type: Boolean, default: false},
  // A day with no time (a date of birth, the day something starts): v-model is
  // "yyyy-MM-dd" in and out, read without a timezone shift. Without it the
  // model is local midnight as a UTC instant, which an API keeping only the
  // date part stores as the day before in summer time (utils/dateOnly).
  dateOnly: {type: Boolean, default: false},
  quick: {
    type: Array,
    default: () => [
      {label: 'Today', days: 0},
      {label: 'Tomorrow', days: 1},
      {label: 'In a week', days: 7},
    ],
  },
});

// ISO in, ISO out (or yyyy-MM-dd with dateOnly). Never the display text.
const model = defineModel();
// The model as the instant the field works in, and back.
const modelIso = computed(() => (props.dateOnly ? dateOnlyForPicker(model.value) : (model.value ?? null)));
const commitIso = (iso) => { model.value = props.dateOnly ? toDateOnly(iso) : iso; };

const {dateInputDisplay, dateInputCommit, dateInputMask, dateInputPlaceholder, currentLocale} =
  useDates();

const deviceLocale = useDeviceLocale();
const activeLocale = computed(() => props.locale || deviceLocale.value || currentLocale());

const maskOptions = computed(() => ({mask: dateInputMask(activeLocale.value), eager: true}));
const placeholder = computed(() => dateInputPlaceholder(activeLocale.value));

// The label carries the order it wants — a box holding "08/12/2026" with
// nothing stating the order is how the wrong date gets read.
const labelWithFormat = computed(() =>
  props.label ? `${props.label} (${placeholder.value})` : null,
);

const menu = ref(false);

const text = ref(dateInputDisplay(modelIso.value, activeLocale.value));
const invalid = ref(false);

// Re-render on an EXTERNAL change only — never while the text already means the
// model, or we would rewrite what someone is part-way through typing.
watch(
  () => [modelIso.value, activeLocale.value],
  () => {
    const current = dateInputCommit(text.value, activeLocale.value);
    if ((current.iso ?? null) !== (modelIso.value ?? null)) {
      text.value = dateInputDisplay(modelIso.value, activeLocale.value);
      invalid.value = false;
    }
  },
);

watch(text, (typed) => {
  const result = dateInputCommit(typed, activeLocale.value);
  invalid.value = result.invalid;
  // A half-deleted field commits null, never the stale previous date.
  commitIso(result.iso);
});

const messages = computed(() => {
  if (invalid.value) return [`Enter a real date as ${placeholder.value}`];
  return props.errorMessages;
});
const hasError = computed(() => props.error || invalid.value);

// ── The calendar half: TapDatePickerCalendar ───────────────────────────────
// A picked day is turned into a stored value by the SAME functions the typed
// half uses, via its display text. It looks like a detour and it is the point:
// a click and a typed date for the same day cannot produce different values,
// because the click goes through the typing path.
const isoFor = (day) =>
  dateInputCommit(dateInputDisplay(day.toJSDate(), activeLocale.value), activeLocale.value).iso;
const commitDay = (day) => {
  text.value = dateInputDisplay(isoFor(day), activeLocale.value);
  menu.value = false;
};
</script>

<template>
  <div>
    <div v-if="labelWithFormat" class="tl-label mb-2">{{ labelWithFormat }}</div>
    <!-- min-width 0 stops v-menu stretching to the activator's width, and the
         content-class strips the default surface panel so only the .tdp card
         shows — without them the calendar floats inside a huge white box. -->
    <v-menu
        v-model="menu"
        :close-on-content-click="false"
        location="bottom start"
        :offset="6"
        :min-width="0"
        content-class="tdp-menu"
    >
      <template #activator="{props: menuProps}">
        <v-text-field
            v-bind="menuProps"
            v-maska="maskOptions"
            v-model="text"
            :placeholder="placeholder"
            prepend-inner-icon="mdi-calendar-blank-outline"
            append-inner-icon="mdi-unfold-more-horizontal"
            :error="hasError"
            :hide-details="!hasError && !hint"
            :error-messages="messages"
            :hint="hint"
            :persistent-hint="hint != null"
        />
      </template>

      <tap-date-picker-calendar :selected-iso="modelIso" :min="min" :max="max" :birth-date="birthDate" :quick="quick" :open="menu" @pick="commitDay"/>
    </v-menu>
  </div>
</template>

<style scoped>
/* X165 (R76 L4): on a phone the field is the tap target already; the unfold icon's 32px are the year's
   ("07/10/202…" was cut behind it in a half-width column at 390) */
@media (max-width: 599px) {
  :deep(.v-field__append-inner) {
    display: none;
  }
}
</style>
